import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { IndicadorStatusPedidosChart } from '../IndicadoresGraficos.types';
import './IndicadoresResumoKpis.css';

type IndicadoresResumoKpisProps = {
    dadosStatusPedidos: IndicadorStatusPedidosChart[];
    totalFaturado: number;
    ticketMedio: number;
    formatarMoeda: (valor: number) => string;
};

type StatusCard = {
    statusPedido: string;
    quantidadePedidos: number;
    total: number;
};

const STATUS_PERMITIDOS = new Set(['orcamento', 'pedido', 'faturado']);

const STATUS_LABEL_PADRAO: Record<string, string> = {
    orcamento: 'Orçamento',
    pedido: 'Pedido',
    faturado: 'Faturado',
};

const STATUS_ORDEM: string[] = ['orcamento', 'pedido', 'faturado'];

function normalizarStatus(status: string): string {
    return status
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
}

function resolverStatusBase(status: string): string {
    return normalizarStatus(status).split('-')[0].trim();
}

function obterIconeStatus(status: string): React.ReactNode {
    switch (normalizarStatus(status)) {
        case 'orcamento': return <AssignmentIcon />;
        case 'pedido': return <ShoppingCartIcon />;
        case 'faturado': return <PaymentIcon />;
        default: return null;
    }
}

function obterCorIconeStatus(status: string): string {
    switch (normalizarStatus(status)) {
        case 'orcamento': return '#95a5a6';
        case 'pedido': return '#3498db';
        case 'faturado': return '#27ae60';
        default: return '#667788';
    }
}

const ICON_BOX_SX = {
    width: 16,
    height: 16,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { width: '14px', height: '14px' },
};

function IndicadoresResumoKpis({
    dadosStatusPedidos,
    totalFaturado,
    ticketMedio,
    formatarMoeda,
}: IndicadoresResumoKpisProps) {
    const cardsStatus = useMemo<StatusCard[]>(() => {
        const acumulado = new Map<string, StatusCard>();

        dadosStatusPedidos.forEach((item) => {
            const statusOriginal = item.statusPedido || '';
            const statusBase = resolverStatusBase(statusOriginal);

            if (!statusBase || !STATUS_PERMITIDOS.has(statusBase)) return;

            const existente = acumulado.get(statusBase);
            const quantidade = Number(item.quantidadePedidos || 0);
            const total = Number(item.total || 0);

            if (existente) {
                existente.quantidadePedidos += quantidade;
                existente.total += total;
            } else {
                acumulado.set(statusBase, { statusPedido: statusOriginal, quantidadePedidos: quantidade, total });
            }
        });

        // Garante que todos os 3 status apareçam, mesmo com valores zerados
        STATUS_ORDEM.forEach((statusBase) => {
            if (!acumulado.has(statusBase)) {
                acumulado.set(statusBase, {
                    statusPedido: STATUS_LABEL_PADRAO[statusBase],
                    quantidadePedidos: 0,
                    total: 0,
                });
            }
        });

        return STATUS_ORDEM.map((statusBase) => acumulado.get(statusBase)!);
    }, [dadosStatusPedidos]);

    return (
        <Box className='indicadores-resumo-kpis'>

            {/* Cards de status */}
            {cardsStatus.map((item) => (
                <Paper
                    key={resolverStatusBase(item.statusPedido)}
                    elevation={0}
                    className='indicadores-paper indicadores-kpi-card'
                    sx={{ minWidth: '128px !important' }}
                >
                    <Box className='indicadores-kpi-row'>
                        <Box>
                            <Typography className='indicadores-kpi-label'>{item.statusPedido}</Typography>
                            <Typography className='indicadores-kpi-value'>{formatarMoeda(item.total)}</Typography>
                        </Box>
                        <Box
                            sx={{
                                ...ICON_BOX_SX,
                                backgroundColor: `${obterCorIconeStatus(item.statusPedido)}20`,
                                color: obterCorIconeStatus(item.statusPedido),
                            }}
                        >
                            {obterIconeStatus(item.statusPedido)}
                        </Box>
                    </Box>
                </Paper>
            ))}


            {/* Card total faturado */}
            <Paper elevation={0} className='indicadores-paper indicadores-kpi-card'>
                <Box className='indicadores-kpi-row'>
                    <Box>
                        <Typography className='indicadores-kpi-label'>Total no Período</Typography>
                        <Typography className='indicadores-kpi-value'>{formatarMoeda(totalFaturado)}</Typography>
                    </Box>
                    <Box className='indicadores-kpi-icon indicadores-kpi-icon-faturado' sx={{ '& svg': { width: '14px', height: '14px' } }}>
                        <PaidRoundedIcon />
                    </Box>
                </Box>
            </Paper>

            {/* Card ticket médio */}
            <Paper elevation={0} className='indicadores-paper indicadores-kpi-card'>
                <Box className='indicadores-kpi-row'>
                    <Box>
                        <Typography className='indicadores-kpi-label'>Ticket Médio no Período</Typography>
                        <Typography className='indicadores-kpi-value'>{formatarMoeda(ticketMedio)}</Typography>
                    </Box>
                    <Box className='indicadores-kpi-icon indicadores-kpi-icon-ticket' sx={{ '& svg': { width: '14px', height: '14px' } }}>
                        <TrendingUpRoundedIcon />
                    </Box>
                </Box>
            </Paper>

        </Box>
    );
}

export default React.memo(IndicadoresResumoKpis);
